// externals
import { connect } from 'react-redux';

// libraries
import { IShopper } from '@makemydeal/dr-platform-types';

// components
import { LeadFormUI } from './LeadFormUI';

// selectors
import { Selectors, setMeta, mergeActionPayload } from '../store';

// interfaces/types
import { IAdditionalSelectors } from '../types/IAdditionalSelectors';

// actions
import {
    onCloseLeadFormModal,
    onLeadFormSubmitClicked,
    onLeadFormCancelClicked,
    onLeadFormSkipClicked,
    onFirstNameBlur as onFirstNameBlurAction,
    onLastNameBlur as onLastNameBlurAction,
    onPhoneBlur as onPhoneBlurAction,
    onEmailBlur as onEmailBlurAction,
    onMessageBlur as onMessageBlurAction,
    onFormSubmit as onFormSubmitAction,
    onReinitializeForm as onReinitializeFormAction,
    Actions
} from '../store';
import { AnalyticsActions } from '../types/AnalyticsActions';
import { isElitePackageEnabled } from '../utils';

export const mapStateToProps = (state, props) => {
    const {
        selector,
        selector: { additionalSelectors }
    }: { selector: Selectors; additionalSelectors: IAdditionalSelectors } = props;
    const shopper: IShopper = additionalSelectors.getShopperInfo(state);
    const dealerZip = additionalSelectors.dealerSelectors.getDealerZip(state);
    const isTCPAEnabled = additionalSelectors.dealerSelectors.getTCPAEnabled(state);
    const isTCPACheckboxDisabled = additionalSelectors.dealerSelectors.getTCPACheckboxStatus(state);
    const isCCPAEnabled = additionalSelectors.dealerSelectors.getCCPAEnabled(state);
    const isDealSummaryPage = additionalSelectors.routerSelectors.getRouterPathName(state) === '/dealsummarypage';
    const isFromBuyOnlineButton = selector.isFromBuyOnlineButton(state);
    const shouldRenderDealerElitePackageLeadForm = isElitePackageEnabled(state, additionalSelectors) && isDealSummaryPage && isFromBuyOnlineButton;
    const isPhoneNumberRequired =
        shouldRenderDealerElitePackageLeadForm || additionalSelectors.dealerSelectors.getPhoneNumberRequiredEnabled(state);
    const CCPAConsentText = additionalSelectors.dealerSelectors.getCCPAConsentText(state);
    const shopperConsentText = additionalSelectors.dealerSelectors.getShopperConsentText(state);
    const shopperTCPAProvider = additionalSelectors.dealerSelectors.getTCPAProvider(state);
    const dealerName = additionalSelectors.dealerSelectors.getDealerName(state);

    const isPaymentTermsFallback = additionalSelectors.offerSelectors.isPaymentTermsFallback(state);
    const title = selector.getTitle(state);
    const description = selector.getDescription(state);
    const skipLink = selector.getSkipLink(state);
    const errorDescription = selector.getErrorDescription(state);
    const externalErrorRetries = selector.getExternalErrorCount(state);
    const successAction = selector.getSuccessAction(state);
    const meta = selector.getMeta(state);
    const leadFormSubmitting = selector.isLeadFormSubmitting(state);
    const leadFormSubmittedSuccess = selector.isLeadFormSubmittedSuccess(state);
    const leadFormSubmittedFailure = selector.isLeadFormSubmittedFailure(state);
    const leadFormReinitialized = selector.isLeadFormReinitialized(state);
    const staticImages = props.staticImages;
    const preSubmitAction = selector.getPreSubmitAction(state);
    const zipCodeLoading = selector.getZipCodeLoading(state);
    const leadFormCCPAShown = selector.isLeadFormCCPAShown(state);
    const isTradePaymentFailed = additionalSelectors.tradeInSelectors.isTradePaymentFailed(state);
    const dealerPhoneNumber = additionalSelectors.dealerSelectors.getDealerPhoneNumber(state);
    const isTCPAInitChecked = selector.isTCPAInitChecked(state);
    const isPhoneRequiredForConsentDisplay = additionalSelectors.dealerSelectors.getPhoneRequiredForTCPADisplayEnabled(state);
    const creditApplicationDisclaimer = 'This is not an online credit application.';
    const isShowCreditApplicationDisclaimer = additionalSelectors.dealerSelectors.showCreditApplicationDisclaimer(state);
    const showShopperConsentText = (isTCPAEnabled && shopper?.phone?.length > 0) || !isPhoneRequiredForConsentDisplay;
    const ecommerceTenantURL = additionalSelectors.dealerSelectors.getEcommerceTenantUrl(state);
    return {
        shopper,
        leadFormSubmitting,
        leadFormSubmittedSuccess,
        leadFormSubmittedFailure,
        leadFormReinitialized,
        isLeadFormOptional: skipLink,
        shopperConsentText,
        isTCPAEnabled,
        isTCPACheckboxDisabled,
        isCCPAEnabled,
        isTCPAInitChecked,
        CCPAConsentText,
        dealerZip,
        title,
        description,
        errorDescription,
        successAction,
        meta,
        staticImages,
        externalErrorRetries,
        preSubmitAction,
        isPhoneNumberRequired,
        zipCodeLoading,
        leadFormCCPAShown,
        isPaymentTermsFallback,
        isTradePaymentFailed,
        dealerPhoneNumber,
        isPhoneRequiredForConsentDisplay,
        creditApplicationDisclaimer,
        isShowCreditApplicationDisclaimer,
        showShopperConsentText,
        dealerName,
        shopperTCPAProvider,
        shouldRenderDealerElitePackageLeadForm,
        ecommerceTenantURL
    };
};

export const mapDispatchToProps = (dispatch, props) => {
    const { actionCreators, dispatchAnalytics } = props;
    const updateShopper = (shopper: IShopper) => {
        dispatch(
            actionCreators.updatedShopper({
                shopper,
                message: shopper.message || ''
            })
        );
    };

    const onSubmit = (shopper: IShopper, meta?: Record<string, unknown>) => {
        dispatch(
            actionCreators.updatedShopper({
                shopper,
                message: shopper.message || ''
            })
        );
        dispatch(onLeadFormSubmitClicked());
        dispatch(onFormSubmitAction());
        dispatch(actionCreators.updateCommunicationPreference());
        actionCreators.checkMessageOnFormSubmit(shopper, dispatch);
        dispatch(actionCreators.checkZipOnFormSubmit(shopper.zip));
        dispatch(mergeActionPayload(actionCreators.submitOffer(true), meta));
        dispatch(setMeta(undefined));
    };

    const onReinitializeForm = () => {
        dispatch(onReinitializeFormAction());
    };

    const dispatchPreSubmitEvent = (preSubmitEvent) => {
        dispatch({
            type: preSubmitEvent
        });
    };

    const onLeadFormSubmittedSuccess = (successAction) => {
        if (successAction) {
            dispatch(onFormSubmitAction());
            dispatch({ type: successAction });
        } else {
            dispatch(onCloseLeadFormModal());
        }
    };

    const onFirstNameStart = (reportAnalytics: boolean) => {
        if (reportAnalytics) {
            dispatch(onFirstNameBlurAction());
        }
    };
    const onFirstNameBlur = (shopper: IShopper, reportAnalytics: boolean) => {
        if (reportAnalytics) {
            dispatch(onFirstNameBlurAction());
        }
        updateShopper(shopper);
    };
    const onLastNameBlur = (shopper: IShopper, reportAnalytics: boolean) => {
        if (reportAnalytics) {
            dispatch(onLastNameBlurAction());
        }
        updateShopper(shopper);
    };
    const onPhoneBlur = (shopper: IShopper, reportAnalytics: boolean) => {
        if (reportAnalytics) {
            dispatch(onPhoneBlurAction());
        }
        updateShopper(shopper);
    };
    const onEmailBlur = (shopper: IShopper, reportAnalytics: boolean) => {
        if (reportAnalytics) {
            dispatch(onEmailBlurAction());
        }
        updateShopper(shopper);
    };
    const onMessageBlur = (shopper: IShopper, reportAnalytics: boolean) => {
        if (reportAnalytics) {
            dispatch(onMessageBlurAction());
        }
        updateShopper(shopper);
    };

    const routeWithoutSubmitting = () => {
        dispatch(onLeadFormCancelClicked());
        dispatch(onLeadFormSkipClicked());
        dispatch(onCloseLeadFormModal());
    };

    const backToDeal = () => {
        dispatch(onLeadFormCancelClicked());
        dispatch(dispatchAnalytics(AnalyticsActions.DR_LEADFORM_SKIPPED, 'Lead form skipped on [CURRENTROUTE]'));
        dispatch(onCloseLeadFormModal());
    };

    const dismissPaymentTermsFallbackMessage = () => {
        dispatch(actionCreators.hidePaymentTermsFallbackMessage());
    };

    const removeTradeInFromSummary = () => {
        dispatch({ type: Actions.SUMMARY_REMOVE_TRADE_IN_CLICKED });
        dispatch(actionCreators.goToNext('/dashboard'));
        dispatch(onCloseLeadFormModal());
    };

    const cdlInitialFormClicked = () => {
        dispatch({ type: Actions.CDL_INITIAL_LEAD_FORM_CLICKED });
    };

    const cdlSubmitFormClicked = () => {
        dispatch({ type: Actions.CDL_SUBMIT_LEAD_FORM_CLICKED });
    };

    const cdlCheckboxChecked = () => {
        dispatch({ type: Actions.CDL_CHECKBOX_CHECKED });
    };

    return {
        updateShopper,
        onSubmit,
        onLeadFormSubmittedSuccess,
        onFirstNameStart,
        onFirstNameBlur,
        onLastNameBlur,
        onPhoneBlur,
        onEmailBlur,
        onMessageBlur,
        routeWithoutSubmitting,
        backToDeal,
        onReinitializeForm,
        dispatchPreSubmitEvent,
        dismissPaymentTermsFallbackMessage,
        removeTradeInFromSummary,
        cdlInitialFormClicked,
        cdlSubmitFormClicked,
        cdlCheckboxChecked
    };
};

export const LeadForm = connect(mapStateToProps, mapDispatchToProps)(LeadFormUI);

export default LeadForm;
